import {Pipe, PipeTransform} from '@angular/core';
import {NiceValuePipe} from '../shared.pipe';
import {I18NDatePipe, I18NTimestampPipe} from '../../i18n/i18n.pipe';
import {I18NService} from '../../i18n/i18n.service';
import {AuthService} from 'auth-commons';
import {ChartLine, SelectionInfo, TextNode} from './line-chart.component';
import {isDefined} from '../../commons/commons';

export const TIME_DELTA_DAY = 86400000;
export const TIME_DELTA_HOUR = 3600000;
export const TIME_DELTA_QUARTER_HOUR = 900000;

@Pipe({
    name: 'yTickFormatter',
    pure: true
})
export class YTickFormatterPipe implements PipeTransform {

    private readonly nvp: NiceValuePipe;

    constructor(private readonly i18n: I18NService) {
        this.nvp = new NiceValuePipe(i18n);
    }

    transform(uom: string, decimals?: number): ((value: number) => string) {
        const dec = uom === '€' ? 2 : decimals;
        return value => this.nvp.transform(value, uom, dec);
    }
}

@Pipe({
    name: 'tooltipFormatter',
    pure: true
})
export class TooltipFormatterPipe implements PipeTransform {

    private readonly nvp: NiceValuePipe;
    private readonly tsp: I18NTimestampPipe;
    private readonly dp: I18NDatePipe;

    constructor(private readonly i18n: I18NService,
                private readonly auth: AuthService) {
        this.nvp = new NiceValuePipe(i18n);
        this.tsp = new I18NTimestampPipe(i18n, auth);
        this.dp = new I18NDatePipe(i18n, auth);
    }

    private static mkSourceInfo(chartLine: ChartLine, data: [number, number]): string {
        if (isDefined(data[1]) && chartLine.fallbackTimes?.has(data[0])) {
            return 'from statistical profile';
        }
        return null;
    }

    transform(uom: string, timeDelta?: number, decimals?: number): (selectionInfo: SelectionInfo) => TextNode[][] {
        return (selectionInfo: SelectionInfo) => {
            const dec = uom === '€' ? 2 : decimals;
            const selData: [number, number][] = [];
            const lines: ChartLine[] = [];
            const selMask: boolean[] = [];
            for (let i = 0; i < selectionInfo.selectedIndices.length; ++i) {
                if (selectionInfo.selectedIndices[i] !== null) {
                    selData.push(selectionInfo.lines[i].data[selectionInfo.selectedIndices[i]]);
                    lines.push(selectionInfo.lines[i]);
                    selMask.push(i === selectionInfo.closestIndex);
                }
            }
            if (!selData.length) {
                return null;
            }
            // if *all* data share the same 'x', gather it at the beginning
            if (selData.every(d => d[0] === selData[0][0])) {
                return [
                    [{text: this.getDateForTooltip(selData[0][0], timeDelta), italic: true}],
                    ...selData.map((d, i) => {
                        const srcInfo = TooltipFormatterPipe.mkSourceInfo(lines[i], d);
                        return [
                            {text: '\u2022   ', color: lines[i].style.color, bold: true},
                            {text: lines[i].name ? lines[i].name + ': ' : '', bold: selMask[i]},
                            {
                                text: isDefined(d[1]) ? this.nvp.transform(d[1], uom, dec) : this.i18n.translate('No data'),
                                bold: true
                            },
                            ...(srcInfo ? [{text: ' (' + this.i18n.translate(srcInfo) + ')'} as TextNode] : [])
                        ];
                    })
                ];
            }
            return selData.map((d, i) => {
                const srcInfo = TooltipFormatterPipe.mkSourceInfo(lines[i], d);
                return [
                    {text: '\u2022   ', color: lines[i].style.color, bold: true},
                    {text: lines[i].name ? lines[i].name + ': ' : ''},
                    {
                        text: isDefined(d[1]) ? this.nvp.transform(d[1], uom, dec) : this.i18n.translate('No data'),
                        bold: true
                    },
                    ...(srcInfo ? [{text: ' (' + this.i18n.translate(srcInfo) + ')'} as TextNode] : []),
                    {text: '  @ '},
                    {text: this.getDateForTooltip(d[0], timeDelta), italic: true}
                ];
            });
        };
    }

    private getDateForTooltip(data: number, timeDelta?: number) {
        if (isDefined(timeDelta)) {
            return timeDelta === TIME_DELTA_DAY ?
                this.dp.transform(data) :
                this.tsp.transform(data) + ' - ' + this.tsp.transform(data + timeDelta);
        }
        return this.tsp.transform(data);
    }
}
